import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import LanguagePicker from "@/components/LanguagePicker";

const lnk = (href, text, external = false) => (
    <a href={href} className="text-[#0b905d] hover:underline" {...(external ? { target: "_blank", rel: "noreferrer" } : {})}>
        {text}
    </a>
);

const CONTENT = {
    de: {
        eyebrow: "Rechtliches",
        title: "Datenschutzerklärung",
        subtitle: "Informationen zur Verarbeitung personenbezogener Daten gemäß Art. 13, 14 DSGVO.",
        back: "Zurück",
        footer: "Stand: Juni 2026",
        sections: [
            {
                title: "1. Verantwortlicher",
                body: (
                    <>
                        <p>Verantwortlicher im Sinne der DSGVO ist:</p>
                        <p className="mt-2"><strong>SideQuestGuy.com</strong></p>
                        <p>Inhaber: Arno Strehle</p>
                        <p>Stresemannplatz 2</p>
                        <p>01309 Dresden, Deutschland</p>
                        <p>Telefon: {lnk("tel:+4935182489029", "+49 351 82489029")}</p>
                        <p>E-Mail: {lnk("mailto:datenschutz@widerruf.online", "datenschutz@widerruf.online")}</p>
                    </>
                ),
            },
            {
                title: "2. Erhebung und Speicherung personenbezogener Daten",
                body: (
                    <>
                        <p>Bei der Nutzung von widerruf.online erheben wir folgende Daten:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                            <li><strong>Registrierung als Händler:</strong> Name, E-Mail, Passwort (gehasht), Firma (optional)</li>
                            <li><strong>Shop-Stammdaten:</strong> Shop-Name, Adresse, Kontakt-Daten, Logo-URL</li>
                            <li><strong>Widerrufsanträge von Kunden:</strong> Name, Anschrift, E-Mail, Bestelldaten, IBAN (sofern angegeben), hochgeladene Anhänge</li>
                            <li><strong>Server-Logfiles:</strong> IP-Adresse, Datum/Uhrzeit, User-Agent, abgerufene Ressource</li>
                        </ul>
                    </>
                ),
            },
            {
                title: "3. Zwecke und Rechtsgrundlagen der Verarbeitung",
                body: (
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Bereitstellung der Plattform und Vertragserfüllung – Art. 6 Abs. 1 lit. b DSGVO</li>
                        <li>Erfüllung gesetzlicher Aufbewahrungs- und Dokumentationspflichten gemäß EU-Richtlinie 2023/2673 – Art. 6 Abs. 1 lit. c DSGVO</li>
                        <li>Wahrung berechtigter Interessen (Sicherheit, Missbrauchsabwehr, Logging) – Art. 6 Abs. 1 lit. f DSGVO</li>
                        <li>Zahlungsabwicklung über Stripe – Art. 6 Abs. 1 lit. b DSGVO</li>
                    </ul>
                ),
            },
            {
                title: "4. Empfänger und Auftragsverarbeiter",
                body: (
                    <>
                        <p>Wir setzen folgende Dienstleister als Auftragsverarbeiter ein:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                            <li><strong>Stripe Payments Europe Ltd., Irland</strong> – Zahlungsabwicklung von Abonnements</li>
                            <li><strong>Resend Inc., USA</strong> – Versand transaktionaler E-Mails (Bestätigungen, Benachrichtigungen)</li>
                            <li><strong>Hetzner Online GmbH, Deutschland</strong> – Hosting und Infrastruktur (Rechenzentren in Deutschland/Finnland)</li>
                        </ul>
                        <p className="mt-3">Mit allen Auftragsverarbeitern bestehen Verträge zur Auftragsverarbeitung gemäß Art. 28 DSGVO.</p>
                    </>
                ),
            },
            {
                title: "5. Datenübermittlung in Drittländer",
                body: <p>Soweit Daten an Empfänger außerhalb der EU/des EWR übermittelt werden (z. B. Resend, USA), erfolgt die Übermittlung auf Grundlage von EU-Standardvertragsklauseln gemäß Art. 46 Abs. 2 lit. c DSGVO sowie ggf. zusätzlicher technischer und organisatorischer Maßnahmen.</p>,
            },
            {
                title: "6. Speicherdauer",
                body: <p>Personenbezogene Daten werden nur so lange gespeichert, wie es zur Erfüllung des jeweiligen Zwecks erforderlich ist oder gesetzliche Aufbewahrungsfristen dies erfordern. Widerrufsanträge werden nach den handels- und steuerrechtlichen Aufbewahrungspflichten (bis zu 10 Jahre) gespeichert.</p>,
            },
            {
                title: "7. Ihre Rechte als betroffene Person",
                body: (
                    <>
                        <p>Sie haben jederzeit das Recht auf:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                            <li>Auskunft (Art. 15 DSGVO)</li>
                            <li>Berichtigung (Art. 16 DSGVO)</li>
                            <li>Löschung (Art. 17 DSGVO)</li>
                            <li>Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
                            <li>Datenübertragbarkeit (Art. 20 DSGVO)</li>
                            <li>Widerspruch (Art. 21 DSGVO)</li>
                        </ul>
                        <p className="mt-3">Zur Ausübung Ihrer Rechte wenden Sie sich an: {lnk("mailto:datenschutz@widerruf.online", "datenschutz@widerruf.online")}</p>
                    </>
                ),
            },
            {
                title: "8. Beschwerderecht bei der Aufsichtsbehörde",
                body: (
                    <>
                        <p>Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu beschweren. Die für unseren Sitz zuständige Behörde ist:</p>
                        <p className="mt-2">
                            <strong>Der Sächsische Datenschutzbeauftragte</strong><br />
                            Devrientstraße 5 · 01067 Dresden<br />
                            E-Mail: {lnk("mailto:saechsdsb@slt.sachsen.de", "saechsdsb@slt.sachsen.de")}<br />
                            Web: {lnk("https://www.saechsdsb.de", "www.saechsdsb.de", true)}
                        </p>
                    </>
                ),
            },
            {
                title: "9. Cookies und Tracking",
                body: <p>widerruf.online verwendet ausschließlich technisch notwendige Cookies/Local-Storage-Einträge (Session-Token, Sprachauswahl). Es findet kein Tracking durch Dritte und keine Werbe-Cookies statt.</p>,
            },
            {
                title: "10. Datensicherheit",
                body: <p>Wir verwenden TLS-Verschlüsselung für die Datenübertragung sowie bcrypt für Passwörter. Zugriffe auf personenbezogene Daten sind nach dem Need-to-know-Prinzip beschränkt.</p>,
            },
            {
                title: "11. Änderungen dieser Datenschutzerklärung",
                body: <p>Wir behalten uns vor, diese Datenschutzerklärung anzupassen, um sie an geänderte Rechtslagen oder bei Änderungen unseres Dienstes anzupassen. Die jeweils aktuelle Fassung ist auf dieser Seite abrufbar.</p>,
            },
        ],
    },
    en: {
        eyebrow: "Legal",
        title: "Privacy Policy",
        subtitle: "Information on the processing of personal data pursuant to Art. 13, 14 GDPR.",
        back: "Back",
        footer: "Last updated: June 2026",
        sections: [
            {
                title: "1. Controller",
                body: (
                    <>
                        <p>The controller within the meaning of the GDPR is:</p>
                        <p className="mt-2"><strong>SideQuestGuy.com</strong></p>
                        <p>Owner: Arno Strehle</p>
                        <p>Stresemannplatz 2</p>
                        <p>01309 Dresden, Germany</p>
                        <p>Phone: {lnk("tel:+4935182489029", "+49 351 82489029")}</p>
                        <p>Email: {lnk("mailto:datenschutz@widerruf.online", "datenschutz@widerruf.online")}</p>
                    </>
                ),
            },
            {
                title: "2. Collection and Storage of Personal Data",
                body: (
                    <>
                        <p>When using widerruf.online, we collect the following data:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                            <li><strong>Merchant registration:</strong> name, email, password (hashed), company (optional)</li>
                            <li><strong>Shop master data:</strong> shop name, address, contact details, logo URL</li>
                            <li><strong>Customer withdrawal requests:</strong> name, address, email, order data, IBAN (if provided), uploaded attachments</li>
                            <li><strong>Server log files:</strong> IP address, date/time, user agent, accessed resource</li>
                        </ul>
                    </>
                ),
            },
            {
                title: "3. Purposes and Legal Bases",
                body: (
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Provision of the platform and performance of the contract – Art. 6 para. 1 lit. b GDPR</li>
                        <li>Compliance with statutory retention and documentation obligations pursuant to EU Directive 2023/2673 – Art. 6 para. 1 lit. c GDPR</li>
                        <li>Legitimate interests (security, fraud prevention, logging) – Art. 6 para. 1 lit. f GDPR</li>
                        <li>Payment processing via Stripe – Art. 6 para. 1 lit. b GDPR</li>
                    </ul>
                ),
            },
            {
                title: "4. Recipients and Data Processors",
                body: (
                    <>
                        <p>We use the following service providers as data processors:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                            <li><strong>Stripe Payments Europe Ltd., Ireland</strong> – payment processing for subscriptions</li>
                            <li><strong>Resend Inc., USA</strong> – sending transactional emails (confirmations, notifications)</li>
                            <li><strong>Hetzner Online GmbH, Germany</strong> – hosting and infrastructure (data centers in Germany/Finland)</li>
                        </ul>
                        <p className="mt-3">All processors have entered into data processing agreements pursuant to Art. 28 GDPR.</p>
                    </>
                ),
            },
            {
                title: "5. Transfer to Third Countries",
                body: <p>Where data is transferred to recipients outside the EU/EEA (e.g. Resend, USA), the transfer is based on EU Standard Contractual Clauses pursuant to Art. 46 para. 2 lit. c GDPR and, where applicable, additional technical and organisational measures.</p>,
            },
            {
                title: "6. Retention Period",
                body: <p>Personal data is stored only for as long as necessary to fulfil the respective purpose or as required by statutory retention periods. Withdrawal requests are stored in accordance with commercial and tax law retention obligations (up to 10 years).</p>,
            },
            {
                title: "7. Your Rights as a Data Subject",
                body: (
                    <>
                        <p>You have the right at any time to:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                            <li>Access (Art. 15 GDPR)</li>
                            <li>Rectification (Art. 16 GDPR)</li>
                            <li>Erasure (Art. 17 GDPR)</li>
                            <li>Restriction of processing (Art. 18 GDPR)</li>
                            <li>Data portability (Art. 20 GDPR)</li>
                            <li>Objection (Art. 21 GDPR)</li>
                        </ul>
                        <p className="mt-3">To exercise your rights, please contact: {lnk("mailto:datenschutz@widerruf.online", "datenschutz@widerruf.online")}</p>
                    </>
                ),
            },
            {
                title: "8. Right to Lodge a Complaint",
                body: (
                    <>
                        <p>You have the right to lodge a complaint with a data protection supervisory authority. The authority responsible for our registered office is:</p>
                        <p className="mt-2">
                            <strong>Der Sächsische Datenschutzbeauftragte</strong><br />
                            Devrientstraße 5 · 01067 Dresden, Germany<br />
                            Email: {lnk("mailto:saechsdsb@slt.sachsen.de", "saechsdsb@slt.sachsen.de")}<br />
                            Web: {lnk("https://www.saechsdsb.de", "www.saechsdsb.de", true)}
                        </p>
                    </>
                ),
            },
            {
                title: "9. Cookies and Tracking",
                body: <p>widerruf.online uses only technically necessary cookies/local storage entries (session token, language preference). No third-party tracking or advertising cookies are used.</p>,
            },
            {
                title: "10. Data Security",
                body: <p>We use TLS encryption for data transmission and bcrypt for passwords. Access to personal data is restricted on a need-to-know basis.</p>,
            },
            {
                title: "11. Changes to this Privacy Policy",
                body: <p>We reserve the right to update this privacy policy to reflect changes in the law or changes to our service. The current version is always available on this page.</p>,
            },
        ],
    },
    fr: {
        eyebrow: "Mentions légales",
        title: "Politique de confidentialité",
        subtitle: "Informations sur le traitement des données personnelles conformément aux art. 13 et 14 du RGPD.",
        back: "Retour",
        footer: "Mise à jour : juin 2026",
        sections: [
            {
                title: "1. Responsable du traitement",
                body: (
                    <>
                        <p>Le responsable du traitement au sens du RGPD est :</p>
                        <p className="mt-2"><strong>SideQuestGuy.com</strong></p>
                        <p>Propriétaire : Arno Strehle</p>
                        <p>Stresemannplatz 2</p>
                        <p>01309 Dresde, Allemagne</p>
                        <p>Téléphone : {lnk("tel:+4935182489029", "+49 351 82489029")}</p>
                        <p>E-mail : {lnk("mailto:datenschutz@widerruf.online", "datenschutz@widerruf.online")}</p>
                    </>
                ),
            },
            {
                title: "2. Collecte et conservation des données personnelles",
                body: (
                    <>
                        <p>Lors de l'utilisation de widerruf.online, nous collectons les données suivantes :</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                            <li><strong>Inscription en tant que marchand :</strong> nom, e-mail, mot de passe (haché), société (optionnel)</li>
                            <li><strong>Données de la boutique :</strong> nom, adresse, coordonnées, URL du logo</li>
                            <li><strong>Demandes de rétractation des clients :</strong> nom, adresse, e-mail, données de commande, IBAN (si fourni), pièces jointes</li>
                            <li><strong>Journaux serveur :</strong> adresse IP, date/heure, user-agent, ressource consultée</li>
                        </ul>
                    </>
                ),
            },
            {
                title: "3. Finalités et bases légales du traitement",
                body: (
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Fourniture de la plateforme et exécution du contrat – art. 6 § 1 lit. b RGPD</li>
                        <li>Respect des obligations légales de conservation et de documentation conformément à la directive UE 2023/2673 – art. 6 § 1 lit. c RGPD</li>
                        <li>Intérêts légitimes (sécurité, prévention des abus, journalisation) – art. 6 § 1 lit. f RGPD</li>
                        <li>Traitement des paiements via Stripe – art. 6 § 1 lit. b RGPD</li>
                    </ul>
                ),
            },
            {
                title: "4. Destinataires et sous-traitants",
                body: (
                    <>
                        <p>Nous faisons appel aux prestataires de services suivants en tant que sous-traitants :</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                            <li><strong>Stripe Payments Europe Ltd., Irlande</strong> – traitement des paiements des abonnements</li>
                            <li><strong>Resend Inc., États-Unis</strong> – envoi d'e-mails transactionnels (confirmations, notifications)</li>
                            <li><strong>Hetzner Online GmbH, Allemagne</strong> – hébergement et infrastructure (centres de données en Allemagne/Finlande)</li>
                        </ul>
                        <p className="mt-3">Des contrats de sous-traitance conformément à l'art. 28 RGPD ont été conclus avec tous les sous-traitants.</p>
                    </>
                ),
            },
            {
                title: "5. Transfert vers des pays tiers",
                body: <p>Dans la mesure où des données sont transmises à des destinataires hors de l'UE/EEE (p. ex. Resend, États-Unis), le transfert s'effectue sur la base des clauses contractuelles types de l'UE conformément à l'art. 46 § 2 lit. c RGPD et, le cas échéant, de mesures techniques et organisationnelles supplémentaires.</p>,
            },
            {
                title: "6. Durée de conservation",
                body: <p>Les données personnelles ne sont conservées que le temps nécessaire à l'accomplissement de la finalité respective ou tant que des délais légaux de conservation l'exigent. Les demandes de rétractation sont conservées conformément aux obligations de conservation du droit commercial et fiscal (jusqu'à 10 ans).</p>,
            },
            {
                title: "7. Vos droits en tant que personne concernée",
                body: (
                    <>
                        <p>Vous disposez à tout moment du droit à :</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                            <li>L'accès (art. 15 RGPD)</li>
                            <li>La rectification (art. 16 RGPD)</li>
                            <li>L'effacement (art. 17 RGPD)</li>
                            <li>La limitation du traitement (art. 18 RGPD)</li>
                            <li>La portabilité des données (art. 20 RGPD)</li>
                            <li>L'opposition (art. 21 RGPD)</li>
                        </ul>
                        <p className="mt-3">Pour exercer vos droits, contactez : {lnk("mailto:datenschutz@widerruf.online", "datenschutz@widerruf.online")}</p>
                    </>
                ),
            },
            {
                title: "8. Droit de réclamation auprès de l'autorité de contrôle",
                body: (
                    <>
                        <p>Vous avez le droit de déposer une réclamation auprès d'une autorité de contrôle de la protection des données. L'autorité compétente pour notre siège est :</p>
                        <p className="mt-2">
                            <strong>Der Sächsische Datenschutzbeauftragte</strong><br />
                            Devrientstraße 5 · 01067 Dresde, Allemagne<br />
                            E-mail : {lnk("mailto:saechsdsb@slt.sachsen.de", "saechsdsb@slt.sachsen.de")}<br />
                            Web : {lnk("https://www.saechsdsb.de", "www.saechsdsb.de", true)}
                        </p>
                    </>
                ),
            },
            {
                title: "9. Cookies et suivi",
                body: <p>widerruf.online utilise uniquement des cookies/entrées de stockage local techniquement nécessaires (jeton de session, préférence de langue). Aucun suivi par des tiers ni cookies publicitaires ne sont utilisés.</p>,
            },
            {
                title: "10. Sécurité des données",
                body: <p>Nous utilisons le chiffrement TLS pour la transmission des données et bcrypt pour les mots de passe. L'accès aux données personnelles est limité selon le principe du besoin d'en connaître.</p>,
            },
            {
                title: "11. Modifications de la présente politique de confidentialité",
                body: <p>Nous nous réservons le droit d'adapter la présente politique de confidentialité en cas d'évolutions législatives ou de modifications de notre service. La version en vigueur est toujours disponible sur cette page.</p>,
            },
        ],
    },
    es: {
        eyebrow: "Legal",
        title: "Política de privacidad",
        subtitle: "Información sobre el tratamiento de datos personales conforme a los arts. 13 y 14 del RGPD.",
        back: "Volver",
        footer: "Actualización: junio de 2026",
        sections: [
            {
                title: "1. Responsable del tratamiento",
                body: (
                    <>
                        <p>El responsable del tratamiento en el sentido del RGPD es:</p>
                        <p className="mt-2"><strong>SideQuestGuy.com</strong></p>
                        <p>Titular: Arno Strehle</p>
                        <p>Stresemannplatz 2</p>
                        <p>01309 Dresde, Alemania</p>
                        <p>Teléfono: {lnk("tel:+4935182489029", "+49 351 82489029")}</p>
                        <p>Correo electrónico: {lnk("mailto:datenschutz@widerruf.online", "datenschutz@widerruf.online")}</p>
                    </>
                ),
            },
            {
                title: "2. Recogida y almacenamiento de datos personales",
                body: (
                    <>
                        <p>Al utilizar widerruf.online, recopilamos los siguientes datos:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                            <li><strong>Registro como comerciante:</strong> nombre, correo electrónico, contraseña (hasheada), empresa (opcional)</li>
                            <li><strong>Datos maestros de la tienda:</strong> nombre, dirección, datos de contacto, URL del logo</li>
                            <li><strong>Solicitudes de desistimiento de clientes:</strong> nombre, dirección, correo, datos del pedido, IBAN (si se indica), archivos adjuntos</li>
                            <li><strong>Registros del servidor:</strong> dirección IP, fecha/hora, user-agent, recurso consultado</li>
                        </ul>
                    </>
                ),
            },
            {
                title: "3. Fines y bases jurídicas del tratamiento",
                body: (
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Prestación de la plataforma y ejecución del contrato – art. 6 apdo. 1 lit. b RGPD</li>
                        <li>Cumplimiento de las obligaciones legales de conservación y documentación conforme a la Directiva UE 2023/2673 – art. 6 apdo. 1 lit. c RGPD</li>
                        <li>Intereses legítimos (seguridad, prevención de abusos, registro de actividad) – art. 6 apdo. 1 lit. f RGPD</li>
                        <li>Procesamiento de pagos a través de Stripe – art. 6 apdo. 1 lit. b RGPD</li>
                    </ul>
                ),
            },
            {
                title: "4. Destinatarios y encargados del tratamiento",
                body: (
                    <>
                        <p>Utilizamos los siguientes proveedores de servicios como encargados del tratamiento:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                            <li><strong>Stripe Payments Europe Ltd., Irlanda</strong> – procesamiento de pagos de suscripciones</li>
                            <li><strong>Resend Inc., EE. UU.</strong> – envío de correos transaccionales (confirmaciones, notificaciones)</li>
                            <li><strong>Hetzner Online GmbH, Alemania</strong> – alojamiento e infraestructura (centros de datos en Alemania/Finlandia)</li>
                        </ul>
                        <p className="mt-3">Se han suscrito contratos de encargo del tratamiento conforme al art. 28 del RGPD con todos los encargados.</p>
                    </>
                ),
            },
            {
                title: "5. Transferencia a terceros países",
                body: <p>En la medida en que se transfieran datos a destinatarios fuera de la UE/EEE (p. ej. Resend, EE. UU.), la transferencia se realiza sobre la base de las cláusulas contractuales tipo de la UE conforme al art. 46 apdo. 2 lit. c RGPD y, en su caso, medidas técnicas y organizativas adicionales.</p>,
            },
            {
                title: "6. Período de conservación",
                body: <p>Los datos personales se almacenan únicamente durante el tiempo necesario para cumplir el fin respectivo o mientras lo exijan los plazos legales de conservación. Las solicitudes de desistimiento se conservan conforme a las obligaciones de conservación del derecho mercantil y fiscal (hasta 10 años).</p>,
            },
            {
                title: "7. Sus derechos como interesado",
                body: (
                    <>
                        <p>Usted tiene en todo momento derecho a:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                            <li>Acceso (art. 15 RGPD)</li>
                            <li>Rectificación (art. 16 RGPD)</li>
                            <li>Supresión (art. 17 RGPD)</li>
                            <li>Limitación del tratamiento (art. 18 RGPD)</li>
                            <li>Portabilidad de los datos (art. 20 RGPD)</li>
                            <li>Oposición (art. 21 RGPD)</li>
                        </ul>
                        <p className="mt-3">Para ejercer sus derechos, póngase en contacto con: {lnk("mailto:datenschutz@widerruf.online", "datenschutz@widerruf.online")}</p>
                    </>
                ),
            },
            {
                title: "8. Derecho de reclamación ante la autoridad de control",
                body: (
                    <>
                        <p>Tiene derecho a presentar una reclamación ante una autoridad de control de protección de datos. La autoridad competente para nuestro domicilio social es:</p>
                        <p className="mt-2">
                            <strong>Der Sächsische Datenschutzbeauftragte</strong><br />
                            Devrientstraße 5 · 01067 Dresde, Alemania<br />
                            Correo electrónico: {lnk("mailto:saechsdsb@slt.sachsen.de", "saechsdsb@slt.sachsen.de")}<br />
                            Web: {lnk("https://www.saechsdsb.de", "www.saechsdsb.de", true)}
                        </p>
                    </>
                ),
            },
            {
                title: "9. Cookies y seguimiento",
                body: <p>widerruf.online utiliza únicamente cookies/entradas de almacenamiento local técnicamente necesarias (token de sesión, preferencia de idioma). No se utiliza ningún seguimiento de terceros ni cookies publicitarias.</p>,
            },
            {
                title: "10. Seguridad de los datos",
                body: <p>Utilizamos cifrado TLS para la transmisión de datos y bcrypt para las contraseñas. El acceso a los datos personales está restringido según el principio de necesidad de conocimiento.</p>,
            },
            {
                title: "11. Cambios en esta política de privacidad",
                body: <p>Nos reservamos el derecho de actualizar esta política de privacidad para adaptarla a cambios legislativos o modificaciones de nuestro servicio. La versión actualizada siempre está disponible en esta página.</p>,
            },
        ],
    },
};

export default function Datenschutz() {
    const { lang } = useI18n();
    const c = CONTENT[lang] || CONTENT.de;

    return (
        <div className="min-h-screen bg-white" data-testid="datenschutz-page">
            <header className="border-b border-gray-200 bg-white/80 backdrop-blur sticky top-0 z-30">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-md bg-[#0b905d] flex items-center justify-center">
                            <span className="text-white font-display font-bold">W</span>
                        </div>
                        <span className="font-display font-bold text-lg tracking-tight text-[#00153f]">
                            widerruf<span className="text-[#0b905d] text-sm font-semibold">.online</span>
                        </span>
                    </Link>
                    <div className="flex items-center gap-3">
                        <LanguagePicker />
                        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#00153f]" data-testid="back-home">
                            <ArrowLeft className="w-4 h-4" /> {c.back}
                        </Link>
                    </div>
                </div>
            </header>

            <article className="max-w-3xl mx-auto px-6 py-16">
                <div className="text-xs font-mono uppercase tracking-widest text-gray-500 mb-3">{c.eyebrow}</div>
                <h1 className="font-display text-4xl font-bold tracking-tight text-[#00153f]">{c.title}</h1>
                <p className="mt-2 text-gray-600">{c.subtitle}</p>

                {c.sections.map((s) => (
                    <Section key={s.title} title={s.title}>{s.body}</Section>
                ))}

                <p className="mt-12 text-xs text-gray-400">{c.footer}</p>
            </article>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section className="mt-10">
            <h2 className="font-display text-xl font-semibold text-[#00153f] mb-3">{title}</h2>
            <div className="text-gray-700 leading-relaxed space-y-1">{children}</div>
        </section>
    );
}
